import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import {
    Sizes,
    PrimaryButton,
    FullScreenTakeover,
    PageBehaviourButton,
    FormattedNumberRaisedSymbol,
} from '@ratehub/base-ui';
import { getGICTypeName, GIC_MESSAGES } from '@ratehub/inv-common';

import { RatePropType } from '../definitions/BestMortgageRatesPropType';
import ProductTableProviderCell from './ProductTableProviderCell';
import ProductTableBestGICRatesMoreDetails from './ProductTableBestGICRatesMoreDetails';


function ProductTableBestGICsRatesLightbox({ rate, onRateClick, onClose }) {
    const intl = useIntl();

    // When the rate is clicked, we open the lead form.
    // Also, also want to close this modal so there aren’t multiple modals open and the user doesn't have to
    // close both modals to get back to the table
    function handleRateClick() {
        onRateClick(rate);
        onClose();
    }

    return (
        <FullScreenTakeover
            displayMode="lightbox"
            onClose={onClose}
            showBranding={false}
            screenReaderLabel={(
                <FormattedMessage
                    id="web-components.best-gic-rates-lightbox.screen-reader-label"
                    defaultMessage="Modal dialogue for showing additional GIC rate information"
                />
            )}
        >
            <Container className="rh-px-1 rh-pb-1">
                <div className="rate-details-container rh-display-flex rh-flex-wrap rh-align-items-flex-start">
                    <div className="rh-flex-shrink-0">
                        <PageBehaviourButton
                            data-name="best-gic-rates-lightbox-rate"
                            size="extra-large"
                            onClick={handleRateClick}
                        >
                            <FormattedNumberRaisedSymbol
                                value={rate.interest / 100}
                                format="percent2"
                                symbolClassName="rh-title-xs"
                            />
                        </PageBehaviourButton>
                    </div>
                    <ProductTableProviderCell
                        className="rh-mt-0_25"
                        rate={rate}
                        isLogoWrapped={false}
                        hasLogoBorder={true}
                    />
                </div>

                <p className="rh-text-s rh-mt-0_5 rh-mb-1 weight-medium">
                    <FormattedMessage
                        {...GIC_MESSAGES.gicTermNameShort}
                        values={{
                            term: rate.term,
                            years: rate.term / 12,
                        }}
                    />
                    {` / ${getGICTypeName(rate.type, intl)}`}
                </p>

                <ProductTableBestGICRatesMoreDetails
                    minimumInvestment={rate.minimumInvestment}
                    isCDIC={rate.isCDIC}
                    redeemable={rate.redeemable}
                    interestCompoundTerm={rate.interestCompoundTerm}
                    interestPaidTerm={rate.interestPaidTerm}
                />

                <PrimaryButton
                    className="rh-mt-1"
                    size="small"
                    message={(
                        <FormattedMessage
                            id="web-components.best-gic-rates-lightbox.cta.get-rate"
                            defaultMessage="Get rate"
                        />
                    )}
                    onClick={handleRateClick}
                    data-name="best-gics-main-cta-lightbox"
                />
            </Container>
        </FullScreenTakeover>
    );
}

ProductTableBestGICsRatesLightbox.propTypes = {
    rate: RatePropType.isRequired,
    onRateClick: PropTypes.func.isRequired,
    onClose: PropTypes.func,
};

const Container = styled.section`
    > .rate-details-container {
        gap: ${Sizes.SPACING.ONE};
    }
`;

export default ProductTableBestGICsRatesLightbox;
